import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { AuthService } from '@services/auth.service';
import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { rosterParticipantFromPanelContext } from '@services/panel-store';
import { TripService } from '@services/trip.service';
import { RosterParticipant } from '../../../models';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-promote-gl',
  imports: [TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './promote-gl.component.html',
  styleUrls: ['./promote-gl.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoteGlComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);

  readonly participant = computed((): RosterParticipant | null =>
    rosterParticipantFromPanelContext(this.panelHost.rosterPanelContext()),
  );

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'promote-gl') {
        this.submitting.set(false);
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  confirm(): void {
    const participant = this.participant();
    const trip = this.tripService.currentTrip.value();
    if (!participant || !trip || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.groupManagementService
      .promoteGl(trip.id, participant.id)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('group-management.panels.promote-gl.success')),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
          await this.authService.logout();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('group-management.panels.promote-gl.error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }
}
